import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Form fields — input, select, textarea (brand manual §16).
 *
 * §16 is explicit that a field is a *control*, not a campaign element: it takes
 * `--radius-control` (8px) and a Host Grotesk label, never the pill and never
 * Eurocat. Depth is borders-first, so a field is separated from its ground by a
 * hairline and never by a shadow.
 *
 * The class logic lives here rather than inside `input.tsx` for the reason every
 * other primitive was split in audit phase 4.0: a pure-TS `cva` module imports
 * nothing but `class-variance-authority`, so Astro can consume it in frontmatter
 * with no React runtime. Before this file existed the marketing site hand-wrote
 * its own field classes (`contatti.astro`), which is the fork
 * `components/brand/README.md` exists to prevent.
 *
 * SIZE, not surface. There is deliberately no `inverse` variant. A petrolio
 * field on a petrolio post is separated only by `--surface-inverse-border`,
 * which is the weakest pairing the palette can produce; and the manual's own
 * argument puts checkable content on the light field (§16's white card). A form
 * on a board therefore lives in a white post, where `default` is correct.
 */
export const fieldVariants = cva(
  [
    'w-full min-w-0 rounded-control border bg-transparent',
    'text-base transition-[color,box-shadow] outline-none md:text-sm',
    'border-input placeholder:text-muted-foreground',
    'selection:bg-primary selection:text-primary-foreground',
    'file:text-foreground file:inline-flex file:h-7 file:border-0',
    'file:bg-transparent file:text-sm file:font-medium',
    'focus-visible:border-ring focus-visible:ring-primary/50 focus-visible:ring-[2px]',
    /* Invalid state carries a border change as well as a ring: a ring alone
       would be state by colour alone (§0). */
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      size: {
        /** The portals' working density — fixed height, tight padding. */
        control: 'h-9 px-3 py-1',
        /**
         * The marketing register: roomier, and no fixed height so the same
         * variant serves a `<textarea>` and a multi-line `<select>`.
         */
        comfortable: 'px-4 py-3',
      },
    },
    defaultVariants: {
      size: 'control',
    },
  },
);

export type FieldVariantProps = VariantProps<typeof fieldVariants>;
